// src/hooks/useReliableSocket.js
import { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';

/**
 * Hook para gestionar conexiones WebSocket confiables con reconexión automática
 * y mejor manejo de eventos para monitoreo de tráfico en tiempo real
 * 
 * @param {Object} options - Opciones de configuración
 * @returns {Object} - Estado y métodos para interactuar con el socket
 */
export function useReliableSocket(options) {
    // Opciones predeterminadas y destructuring de opciones básicas para determinar conexión física
    const {
        url = process.env.REACT_APP_SOCKET_URL || '',
        authToken = null,
        clientId = null,
        enabled = true
    } = options || {};

    // Estado para rastrear conexión y datos
    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    const [lastPing, setLastPing] = useState(null);
    const [socketId, setSocketId] = useState(null);

    // Referencias para intervalos, timeouts y socket
    const socketRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const pingTimerRef = useRef(null);
    const reconnectCountRef = useRef(0);
    const isCleaningUpRef = useRef(false);
    const subscribedEventsRef = useRef(new Set());
    const lastEventTimestampsRef = useRef({});

    // Opciones secundarias y callbacks almacenados en referencias para evitar re-creación del socket
    const optionsRef = useRef({});
    
    // Mantener la referencia actualizada con los últimos callbacks
    useEffect(() => {
        optionsRef.current = {
            reconnectionAttempts: options?.reconnectionAttempts ?? 15,
            reconnectionDelay: options?.reconnectionDelay ?? 1000,
            pingInterval: options?.pingInterval ?? 10000,
            onConnect: options?.onConnect,
            onDisconnect: options?.onDisconnect,
            onError: options?.onError,
            eventHandlers: options?.eventHandlers ?? {}
        };
    }, [options]);

    // Limpiar recursos
    const cleanup = useCallback(() => {
        isCleaningUpRef.current = true;

        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }

        if (pingTimerRef.current) {
            clearInterval(pingTimerRef.current);
            pingTimerRef.current = null;
        }

        // Cerrar conexión existente
        if (socketRef.current) {
            try {
                if (typeof socketRef.current.removeAllListeners === 'function') {
                    socketRef.current.removeAllListeners();
                }
                socketRef.current.disconnect();
                console.log('Socket desconectado durante limpieza');
            } catch (err) {
                console.warn('Error al cerrar socket:', err);
            }
            socketRef.current = null;
        }

        // Limpiar estado
        setIsConnected(false);
        setSocketId(null);
        subscribedEventsRef.current.clear();
    }, []);

    // Inicializar Socket.IO con configuración óptima
    const initSocket = useCallback(() => {
        if (isCleaningUpRef.current || !enabled) return;

        // Limpiar conexión anterior si existe
        if (socketRef.current) {
            cleanup();
        }

        // Verificar token
        if (!authToken) {
            console.warn('Intentando conectar sin token de autenticación');
            setConnectionError('No hay token de autenticación disponible');
            return;
        }

        console.log('Inicializando nueva conexión socket...');

        try {
            // Preparar datos de autenticación
            const authData = { token: authToken };
            if (clientId) {
                authData.clientId = clientId;
            }

            const currentOpts = optionsRef.current;

            // Crear socket con mejores opciones
            socketRef.current = io(url, {
                auth: authData,
                reconnection: true,
                reconnectionAttempts: currentOpts.reconnectionAttempts,
                reconnectionDelay: currentOpts.reconnectionDelay,
                reconnectionDelayMax: 5000,
                timeout: 20000,
                transports: ['websocket', 'polling'],
                forceNew: false,
                autoConnect: true
            });

            // Configurar eventos básicos
            socketRef.current.on('connect', () => {
                if (isCleaningUpRef.current) return;

                console.log(`Socket conectado con ID: ${socketRef.current.id}`);
                setIsConnected(true);
                setSocketId(socketRef.current.id);
                setConnectionError(null);
                reconnectCountRef.current = 0;
                setLastPing(Date.now());

                // Invocar callback de conexión
                if (typeof optionsRef.current.onConnect === 'function') {
                    optionsRef.current.onConnect(socketRef.current);
                }

                // Iniciar ping para mantener conexión
                if (pingTimerRef.current) {
                    clearInterval(pingTimerRef.current);
                }

                pingTimerRef.current = setInterval(() => {
                    if (socketRef.current?.connected) {
                        socketRef.current.emit('ping');
                    }
                }, optionsRef.current.pingInterval || 10000);

                // Restaurar suscripciones activas
                if (subscribedEventsRef.current.size > 0) {
                    console.log('Restaurando suscripciones activas');

                    // Re-suscribir a eventos de tráfico
                    if (subscribedEventsRef.current.has('traffic')) {
                        socketRef.current.emit('subscribe:traffic',
                            clientId ? { clientId } : undefined);
                    }
                }
            });

            // Manejar desconexión
            socketRef.current.on('disconnect', (reason) => {
                if (isCleaningUpRef.current) return;

                console.log(`Socket desconectado. Razón: ${reason}`);
                setIsConnected(false);

                // Invocar callback de desconexión
                if (typeof optionsRef.current.onDisconnect === 'function') {
                    optionsRef.current.onDisconnect(reason);
                }
            });

            // Manejar errores de conexión
            socketRef.current.on('connect_error', (error) => {
                if (isCleaningUpRef.current) return;

                console.error('Error de conexión socket:', error.message);
                setConnectionError(error.message);
                setIsConnected(false);

                // Invocar callback de error
                if (typeof optionsRef.current.onError === 'function') {
                    optionsRef.current.onError(error);
                }
            });

            // Manejar pongs para medir latencia
            socketRef.current.on('pong', (data) => {
                if (isCleaningUpRef.current) return;
                setLastPing(Date.now());
            });

            // Manejar errores del servidor
            socketRef.current.on('error', (error) => {
                if (isCleaningUpRef.current) return;

                console.error('Error en socket:', error);
                if (typeof optionsRef.current.onError === 'function') {
                    optionsRef.current.onError(error);
                }
            });

            // Registrar handlers personalizados
            const handlers = optionsRef.current.eventHandlers || {};
            Object.entries(handlers).forEach(([event]) => {
                try {
                    // Skip events already handled above
                    if (['connect', 'disconnect', 'connect_error', 'error', 'pong'].includes(event)) {
                        return;
                    }

                    // Register handler
                    socketRef.current.on(event, (...args) => {
                        if (isCleaningUpRef.current) return;

                        // Registrar timestamp
                        lastEventTimestampsRef.current[event] = Date.now();

                        // Ejecutar handler dinámico desde la referencia más reciente
                        const latestHandler = optionsRef.current.eventHandlers?.[event];
                        if (typeof latestHandler === 'function') {
                            latestHandler(...args);
                        }
                    });
                } catch (e) {
                    console.error(`Error al registrar handler para evento ${event}:`, e);
                }
            });

        } catch (error) {
            console.error('Error al inicializar socket:', error);
            setConnectionError(error.message);
            setIsConnected(false);
            socketRef.current = null;
            if (typeof optionsRef.current.onError === 'function') {
                optionsRef.current.onError(error);
            }
        }
    }, [url, authToken, clientId, enabled, cleanup]);



    // Conectar cuando cambian parametros relevantes
    useEffect(() => {
        isCleaningUpRef.current = false;

        if (enabled) {
            initSocket();
        }

        return cleanup;
    }, [enabled, url, authToken, clientId, initSocket, cleanup]);

    // Proveer una función para suscribirse a eventos de tráfico
    const subscribeToTraffic = useCallback((targetClientId = null) => {
        if (!socketRef.current?.connected) {
            console.warn('No se puede suscribir a tráfico: socket no conectado');
            return false;
        }

        console.log(`Suscribiendo a tráfico${targetClientId ? ' para cliente ' + targetClientId : ''}`);

        try {
            const data = targetClientId ? { clientId: targetClientId } : undefined;
            socketRef.current.emit('subscribe:traffic', data);
            subscribedEventsRef.current.add('traffic');

            // Solicitar datos actuales
            socketRef.current.emit('get:traffic', data);

            return true;
        } catch (error) {
            console.error('Error al suscribirse a tráfico:', error);
            return false;
        }
    }, []);

    // Proveer una función para cancelar suscripción
    const unsubscribeFromTraffic = useCallback((targetClientId = null) => {
        if (!socketRef.current?.connected) {
            console.warn('No se puede cancelar suscripción: socket no conectado');
            return false;
        }

        console.log(`Cancelando suscripción a tráfico${targetClientId ? ' para cliente ' + targetClientId : ''}`);

        try {
            const data = targetClientId ? { clientId: targetClientId } : undefined;
            socketRef.current.emit('unsubscribe:traffic', data);
            subscribedEventsRef.current.delete('traffic');
            return true;
        } catch (error) {
            console.error('Error al cancelar suscripción a tráfico:', error);
            return false;
        }
    }, []);

    // Proveer una función para enviar eventos
    const emit = useCallback((event, data) => {
        if (!socketRef.current?.connected) {
            console.warn(`No se puede emitir ${event}: socket no conectado`);
            return false;
        }

        try {
            socketRef.current.emit(event, data);
            return true;
        } catch (error) {
            console.error(`Error al emitir ${event}:`, error);
            return false;
        }
    }, []);

    // Verificar la conexión periódicamente y reconectar si es necesario
    useEffect(() => {
        if (!enabled) return;

        const checkConnectionInterval = setInterval(() => {
            if (isCleaningUpRef.current) return;

            // Si no hay conexión activa y no hay un intento de reconexión en curso
            if (!isConnected && !reconnectTimerRef.current && socketRef.current === null) {
                console.log('Detectada desconexión sin intento de reconexión, iniciando socket...');
                initSocket();
            }

            // Verificar tiempo desde último ping/pong
            if (isConnected && lastPing) {
                const now = Date.now();
                const pingAge = now - lastPing;

                // Si han pasado más de 30 segundos sin ping/pong, forzar reconexión
                if (pingAge > 30000) {
                    console.warn(`No se ha recibido ping/pong en ${pingAge}ms, forzando reconexión`);

                    if (socketRef.current) {
                        socketRef.current.disconnect();
                        socketRef.current = null;
                    }

                    setIsConnected(false);
                    initSocket();
                }
            }
        }, 15000); // Verificar cada 15 segundos

        return () => clearInterval(checkConnectionInterval);
    }, [enabled, isConnected, lastPing, initSocket]);

    // Proveer una función para verificar el estado del socket
    const getStatus = useCallback(() => {
        return {
            connected: isConnected,
            socketId: socketId,
            error: connectionError,
            lastPing: lastPing ? new Date(lastPing) : null,
            reconnectAttempts: reconnectCountRef.current,
            subscribedEvents: [...subscribedEventsRef.current]
        };
    }, [isConnected, socketId, connectionError, lastPing]);

    // Devolver objeto con estado y métodos
    return {
        socket: socketRef.current,
        isConnected,
        error: connectionError,
        emit,
        subscribeToTraffic,
        unsubscribeFromTraffic,
        getStatus
    };
}

export default useReliableSocket;